// The price table, read from the daemon instead of from this bundle.
//
// Two callers, one hook: the public sales page (which must be quoted the same
// numbers a visitor sees) and the operator's Settings → Planos panel (which
// writes them). Keeping both on one fetch is what guarantees the panel is
// editing exactly what the page shows — a second source would drift the day
// someone changed one of them.
//
// `editable` comes from the daemon, never from a guess in the browser. The rule
// for who may write lives in `OD_OPERATOR_EMAILS` on the server and is checked
// against a verified token; a client-side check would be a suggestion.

import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  PlanPricing,
  PlanPricingResponse,
  PlanPricingSource,
} from '@open-design/contracts';

export interface PlanPricingState {
  /** `null` until the first response lands — not an empty table. */
  pricing: PlanPricing[] | null;
  editable: boolean;
  /** Whether this caller is an operator — decides if the panel renders at all. */
  isOperator: boolean;
  source: PlanPricingSource | null;
  updatedAt: string | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  save: (plans: PlanPricing[]) => Promise<boolean>;
  reset: () => Promise<boolean>;
}

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { error?: { message?: string } };
    return body?.error?.message ?? fallback;
  } catch {
    return fallback;
  }
}

export function usePlanPricing(): PlanPricingState {
  const [pricing, setPricing] = useState<PlanPricing[] | null>(null);
  const [editable, setEditable] = useState(false);
  const [isOperator, setIsOperator] = useState(false);
  const [source, setSource] = useState<PlanPricingSource | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A response that arrives after the component is gone must not set state,
  // and — more importantly here — a slow first load must not overwrite a save
  // the operator already made.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const absorb = useCallback((payload: PlanPricingResponse) => {
    if (!alive.current) return;
    setPricing(payload.plans);
    setEditable(payload.editable);
    setIsOperator(payload.isOperator);
    setSource(payload.source);
    setUpdatedAt(payload.updatedAt);
    setError(null);
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/plans');
      if (!response.ok) {
        if (alive.current) setError(await readError(response, 'Não foi possível ler os planos.'));
        return;
      }
      absorb((await response.json()) as PlanPricingResponse);
    } catch {
      // The sales page still renders without this: every tier falls back to
      // "Preço a definir", which is the honest state when the price is unknown.
      if (alive.current) setError('Não foi possível falar com o servidor.');
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [absorb]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const save = useCallback(
    async (plans: PlanPricing[]): Promise<boolean> => {
      setError(null);
      try {
        const response = await fetch('/api/plans', {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ plans }),
        });
        if (!response.ok) {
          setError(await readError(response, 'Não foi possível salvar os planos.'));
          return false;
        }
        absorb((await response.json()) as PlanPricingResponse);
        return true;
      } catch {
        setError('Não foi possível falar com o servidor.');
        return false;
      }
    },
    [absorb],
  );

  const reset = useCallback(async (): Promise<boolean> => {
    setError(null);
    try {
      const response = await fetch('/api/plans/reset', { method: 'POST' });
      if (!response.ok) {
        setError(await readError(response, 'Não foi possível limpar os planos.'));
        return false;
      }
      absorb((await response.json()) as PlanPricingResponse);
      return true;
    } catch {
      setError('Não foi possível falar com o servidor.');
      return false;
    }
  }, [absorb]);

  return { pricing, editable, isOperator, source, updatedAt, loading, error, reload, save, reset };
}
